/* eslint-disable indent */
import React, { useContext, useEffect, useMemo, useRef, useState } from 'react'
import PropTypes from 'prop-types'
import { BoltIcon } from '@heroicons/react/24/outline'
import { ArrowPathIcon } from '@heroicons/react/20/solid'
import _ from 'lodash'
import dayjs from 'dayjs'

// Components
import { Button } from '../Button'
import { DataTable, CUSTOM_FILTER_OPTIONS } from '../DataTable'
import { Modal } from '../Modal'
import { renderMultiSelectFilter } from '../CustomEditor'

// Store
import { TaskStoreContext } from '../../stores/TaskStore'

// Utils & Services
import {
  createClientQcCheckExport,
  getClientPortfolioDataPeriods,
  getClientQcCheck,
  getClientQcCheckFlags,
} from '../../services/clients.service'
import { configureFilterQuery, hasAnyFilterSelected, toast } from '../../utils/helpers'
import { usePagination, useSorting } from '../../hooks/DataTableManagement'

const DEFAULT_FILTERS = {
  portfolio_data__period: { value: [] },
}

const QcCheckModal = ({ client, closeModal, qcCheckId }) => {
  // Context
  const { addTask } = useContext(TaskStoreContext)

  // State
  const [loading, setLoading] = useState(false)
  const [loadingFlags, setLoadingFlags] = useState(true)
  const [loadingExport, setLoadingExport] = useState(false)
  const [qcCheck, setQcCheck] = useState({})
  const [flags, setFlags] = useState([])
  const [periods, setPeriods] = useState([])
  const [filters, setFilters] = useState(DEFAULT_FILTERS)

  // Sorting
  const { sorting } = useSorting('portfolio_data__period')
  const { sortedColumn } = sorting

  // Pagination
  const { pagination, setTotalRecords } = usePagination(50)
  const { perPage, currentPage } = pagination

  const handleSuccess = (m) => toast(m, 'success')
  const handleErrors = (m) => toast(m, 'error')

  const dataTable = useRef()

  const getUpdatedFlags = async (url) => {
    const response = await getClientQcCheckFlags(url, handleErrors, () => {})

    if (response) {
      setTotalRecords(response.count)
      setFlags(response.results)
      setLoadingFlags(false)
    }
  }

  useEffect(() => {
    const getUpdatedData = async () => {
      setLoading(true)
      const [updatedQcCheck, updatedPeriods] = await Promise.all([
        getClientQcCheck(client.id, qcCheckId, handleErrors),
        getClientPortfolioDataPeriods(client.id, handleErrors),
      ])

      if (updatedQcCheck) setQcCheck(updatedQcCheck)
      if (updatedPeriods)
        setPeriods(
          _.map(updatedPeriods, (d) => ({
            label: dayjs(d).format('MM/DD/YYYY'),
            value: d,
            id: d,
          })),
        )

      setLoading(false)
    }

    getUpdatedData()
  }, [qcCheckId])

  /**
   * When the filter, sort, current page, or row count changes, get the updated list of aum data.
   */
  useEffect(() => {
    if (qcCheck) {
      const queryFilters = configureFilterQuery({
        portfolio_data__period:
          filters.portfolio_data__period || DEFAULT_FILTERS.portfolio_data__period,
      })

      getUpdatedFlags(
        `/clients/${client.id}/qc-checks/${qcCheckId}/flags?expand=portfolio_data&order_by=${sortedColumn}&limit=${perPage}&page=${currentPage}&${queryFilters}`,
      )
    }
  }, [qcCheck, perPage, currentPage, sortedColumn, filters])

  const actions = useMemo(() => {
    const options = [{ type: 'cancel', label: 'Cancel', onClick: closeModal }]

    if (flags.length > 0) {
      options.push({
        type: 'submit',
        label: 'Export QC Check',
        onClick: async () => {
          const response = await createClientQcCheckExport(
            client.id,
            qcCheck.id,
            handleErrors,
            setLoadingExport,
            handleSuccess,
          )

          if (response) {
            addTask({
              type: 'qc-export',
              id: response.id,
              data: response,
              downloaded: false,
              fileName: `${client.name}_QC_Check.xlsx`,
            })
            closeModal()
          }
        },
      })
    }

    return options
  }, [flags])

  const renderQcCheck = () => {
    if (!qcCheck || (!loadingFlags && flags?.length === 0)) {
      return <span className="text-center text-sm">No additional flags.</span>
    }

    const benchmarkFlags = _.filter(flags, (f) => f.type === 'Benchmark Check')
    const filteredFlags = _.filter(flags, (f) => f.type !== 'Benchmark Check')
    return (
      <div className="flex size-full flex-col gap-2 overflow-y-auto">
        <div className="flex flex-row items-start justify-between">
          <div className="mb-2 flex flex-col">
            <div className="mt-2 flex flex-col">
              <span className="font-semibold">General Checks:</span>
              {_.join(
                _.map(
                  _.filter(
                    ['benchmarkDataOut', 'compositeCheck', 'eoMonthCheck'],
                    (k) => qcCheck[k],
                  ),
                  (k) => _.startCase(k),
                ),
                ', ',
              )}
            </div>

            {benchmarkFlags.length > 0 && (
              <div className="mt-2">
                <span className="font-semibold">Benchmark Checks:</span>
                <ul>
                  {_.map(benchmarkFlags, (b) => (
                    <li className="list-inside list-disc" key={b.id}>
                      {b.question}
                    </li>
                  ))}
                </ul>
              </div>
            )}
          </div>

          {hasAnyFilterSelected(filters) && (
            <Button
              className="mt-2"
              label="Clear Filters"
              background="bg-gray"
              onClick={() => {
                setFilters(DEFAULT_FILTERS)
              }}
            />
          )}
        </div>

        <div className="h-[450px] w-full">
          <DataTable
            columns={[
              {
                field: 'portfolioData.composite.name',
                header: 'Composite',
                style: { minWidth: '250px' },
              },
              {
                field: 'portfolioData.period',
                header: 'Period',
                body: (row) => dayjs(row.portfolioData.period).format('MM/DD/YYYY'),
                style: { minWidth: '150px' },
                sortField: 'portfolio_data__period',
                sortable: true,
                filterField: 'portfolio_data__period',
                ...CUSTOM_FILTER_OPTIONS,
                filterElement: (options) =>
                  renderMultiSelectFilter(
                    periods,
                    (selected) => {
                      setFilters((prevFilters) => ({
                        ...prevFilters,
                        portfolio_data__period: {
                          value: selected,
                        },
                      }))
                    },
                    'Filter by Period',
                    options,
                  ),
              },
              {
                field: 'portfolioData.account.name',
                header: 'Account Name',
                style: { minWidth: '200px' },
              },
              {
                field: 'portfolioData.account.number',
                header: 'Account Number',
                style: { minWidth: '200px' },
              },
              {
                field: 'type',
                header: 'Flag',
                style: { minWidth: '200px' },
              },
              {
                field: 'question',
                header: 'Question',
                style: { minWidth: '300px' },
              },
            ]}
            data={filteredFlags}
            loading={loadingFlags}
            rowsPerPageOptions={[50, 100, 500, 1000]}
            pagination={pagination}
            ref={dataTable}
            filters={filters}
            onFilter={(e) => setFilters(e.filters)}
            scrollHeight="385px"
            sorting={sorting}
          />
        </div>
      </div>
    )
  }

  return (
    <Modal
      className="sm:w-[90vw] sm:max-w-screen-xl"
      icon={<BoltIcon className="h-6 fill-white stroke-white" />}
      open
      title="QC Check"
      loading={loadingExport}
      onClose={closeModal}
      content={
        <div className="flex size-full flex-col gap-4 pt-2">
          {loading ? (
            <div className="flex size-full flex-col items-center justify-center space-y-2 py-2">
              <span className="text-lg font-bold">Loading...</span>

              <span className="flex items-center pr-3">
                <div className="size-8">
                  {/* eslint-disable-next-line tailwindcss/no-custom-classname, tailwindcss/classnames-order */}
                  <svg className="size-8 motion-safe:animate-spin-slow" viewBox="0 0 40 40">
                    <ArrowPathIcon className="size-8" aria-hidden="true" />
                  </svg>
                </div>
              </span>
            </div>
          ) : (
            renderQcCheck()
          )}
        </div>
      }
      actions={actions}
    />
  )
}

QcCheckModal.propTypes = {
  client: PropTypes.object.isRequired,
  closeModal: PropTypes.func.isRequired,
  qcCheckId: PropTypes.string.isRequired,
}

export default QcCheckModal
